import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Activity, ClipboardList, Plus, Save, Stethoscope } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const BLANK = { time: "", bp: "", pulse: "", temp: "", spo2: "", procedure: "", nurse_note: "" };

export default function IPChart() {
  const { id } = useParams();
  const { user } = useAuth();
  const [adm, setAdm] = useState(null);
  const [entries, setEntries] = useState([]);
  const [row, setRow] = useState({ ...BLANK, time: new Date().toISOString().slice(11, 16) });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admissions/${id}`);
      setAdm(data);
      setEntries(data.chart_entries || []);
    } catch (err) { toast.error(formatApiError(err, "Load failed")); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!row.time && !row.procedure && !row.nurse_note && !row.bp) { toast.error("Enter at least one field"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admissions/${id}/chart-entry`, row);
      setEntries(data.chart_entries || []);
      setRow({ ...BLANK, time: new Date().toISOString().slice(11, 16) });
      toast.success("Chart entry added");
    } catch (err) { toast.error(formatApiError(err, "Save failed")); }
    finally { setBusy(false); }
  };

  if (!adm) return <div className="p-8 text-sm text-[#5C5652]">Loading chart…</div>;

  return (
    <div className="max-w-5xl mx-auto animate-fade-up" data-testid="ip-chart-page">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-2 ornament-dot">IP daily chart · {adm.admission_no}</div>
          <h1 className="font-display text-3xl text-forest">{adm.patient_name}</h1>
          <p className="text-[#5C5652] text-sm">Room {adm.room_number} · {adm.reason || "—"} · MRN {adm.mrn}</p>
        </div>
        <Link to="/manager/ip" data-testid="ip-chart-back" className="h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> IP list</Link>
      </div>

      <div className="bg-white border border-sand rounded-lg p-5 mb-6" data-testid="ip-chart-form">
        <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-3 ornament-dot">New entry</div>
        <div className="grid gap-2 sm:grid-cols-6">
          <input type="time" data-testid="chart-time" value={row.time} onChange={(e) => setRow({ ...row, time: e.target.value })} className="h-10 px-2 rounded-lg border border-sand text-sm" />
          <input placeholder="BP (120/80)" data-testid="chart-bp" value={row.bp} onChange={(e) => setRow({ ...row, bp: e.target.value })} className="h-10 px-2 rounded-lg border border-sand text-sm" />
          <input placeholder="Pulse" data-testid="chart-pulse" value={row.pulse} onChange={(e) => setRow({ ...row, pulse: e.target.value })} className="h-10 px-2 rounded-lg border border-sand text-sm" />
          <input placeholder="Temp °F" data-testid="chart-temp" value={row.temp} onChange={(e) => setRow({ ...row, temp: e.target.value })} className="h-10 px-2 rounded-lg border border-sand text-sm" />
          <input placeholder="SpO₂ %" data-testid="chart-spo2" value={row.spo2} onChange={(e) => setRow({ ...row, spo2: e.target.value })} className="h-10 px-2 rounded-lg border border-sand text-sm" />
          <input placeholder="Procedure" data-testid="chart-procedure" value={row.procedure} onChange={(e) => setRow({ ...row, procedure: e.target.value })} className="h-10 px-2 rounded-lg border border-sand text-sm" />
        </div>
        <textarea rows={2} placeholder="Nurse note / observations" data-testid="chart-note" value={row.nurse_note} onChange={(e) => setRow({ ...row, nurse_note: e.target.value })} className="mt-2 w-full px-3 py-2 rounded-lg border border-sand text-sm resize-none" />
        <button onClick={add} disabled={busy} data-testid="chart-save" className="mt-3 h-10 px-4 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2 disabled:opacity-60">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save entry"}
        </button>
      </div>

      <div className="bg-white border border-sand rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-sand text-xs font-bold uppercase tracking-wider text-[#5C5652]">Timeline · {entries.length} entries</div>
        {entries.length === 0 ? <div className="p-8 text-sm text-center text-[#5C5652]">No entries yet. Add the first vitals reading above.</div>
          : <table className="w-full text-sm" data-testid="chart-timeline">
              <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652]">
                <tr><th className="text-left px-4 py-2">Time</th><th className="text-left px-4 py-2">BP</th><th className="text-left px-4 py-2">Pulse</th><th className="text-left px-4 py-2">Temp</th><th className="text-left px-4 py-2">SpO₂</th><th className="text-left px-4 py-2">Procedure</th><th className="text-left px-4 py-2">Note</th><th className="text-left px-4 py-2">By</th></tr>
              </thead>
              <tbody className="divide-y divide-[#E8E3D9]">
                {entries.slice().reverse().map((e, i) => (
                  <tr key={i} data-testid={`chart-row-${i}`}>
                    <td className="px-4 py-2 font-mono text-xs">{e.time}<br/><span className="text-[9px] text-[#5C5652]">{(e.at || "").slice(0, 10)}</span></td>
                    <td className="px-4 py-2 font-mono text-xs">{e.bp || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{e.pulse || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{e.temp || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{e.spo2 || "—"}</td>
                    <td className="px-4 py-2 text-xs">{e.procedure || "—"}</td>
                    <td className="px-4 py-2 text-xs">{e.nurse_note || "—"}</td>
                    <td className="px-4 py-2 text-[10px] text-[#5C5652]">{e.added_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
    </div>
  );
}
